import PropTypes from "prop-types"
import React, { useState, useEffect, useMemo, useCallback } from "react"
import DataTable from "ui/DataTable"
import DataTableResponsive from "ui/DataTableResponsive"
import { Modal, List, Spin, Card, Empty, Flex, Button } from "antd"
import { DownloadOutlined } from "@ant-design/icons"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import styles from "./index.module.css"
import CustomPagination from "components/common/CustomPagination"
import { getSignedDocuments, getBookingWaivers } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { useDevice } from "hooks/useDevice"
import { DigitalWaiverEmptyImage } from "ui/Icon/DSIcon/DigitalWaiverEmptyImage"
import { Text } from "ui/Typography"
import { SearchIcon } from "icons"
const SignedDocumentsTable = ({ searchValue, selectedExperiences }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isMobile, isTablet } = useDevice()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [sortParams, setSortParams] = useState({})
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedWaiverNames, setSelectedWaiverNames] = useState([])
  const [bookingDocs, setBookingDocs] = useState([])
  const [docsLoading, setDocsLoading] = useState(false)
  const { id } = useSelector((store) => store?.user.guide) || {}
  const { items, loading, metadata } = useSelector((state) => state.digitalWaiver.signedDocuments)
  const isCompact = isMobile || isTablet

  const parseWaiverNames = (waiverName) =>
    waiverName
      ?.split(",")
      ?.map((name) => name.trim())
      ?.filter((name) => name) || []

  const handleRowClick = (_, record) => {
    const uuid = record?.uuid
    if (uuid) {
      navigate(routes.guideBookingDetailsPath({ id: uuid }))
    }
  }

  const handleWaiverNamesClick = useCallback((waiverName, record) => {
    setSelectedWaiverNames(parseWaiverNames(waiverName))
    setModalVisible(true)
    if (record?.uuid) {
      setDocsLoading(true)
      dispatch(getBookingWaivers(record.uuid))
        .then((result) => {
          let docs = []
          if (Array.isArray(result?.data)) docs = result.data
          else if (Array.isArray(result)) docs = result
          setBookingDocs(docs.flatMap((item) => item?.signed_documents || []))
        })
        .finally(() => setDocsLoading(false))
    }
  }, [dispatch])

  const handleModalClose = () => {
    setModalVisible(false)
    setSelectedWaiverNames([])
    setBookingDocs([])
    setDocsLoading(false)
  }

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current: page
    }))
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const newSortParams = {}
    if (sorter?.field) {
      newSortParams[sorter.field] = sorter.order === "ascend" ? "asc" : "desc"
    }
    setSortParams(newSortParams)
    setPagination((prev) => ({
      ...prev,
      current: 1 // Reset to first page when sorting
    }))
  }

  // Fetch data when page, page size, search value, selected experiences, or sort params changes
  // searchValue and selectedExperiences always fetch from page 1
  useEffect(() => {
    const filteredExperiences = selectedExperiences.filter((id) => id !== "all")
    const experienceIds = filteredExperiences.length > 0 ? filteredExperiences : null
    const sortField = Object.keys(sortParams)[0]
    const sortOrder = sortParams[sortField]
    dispatch(getSignedDocuments(id, 1, pagination.pageSize, searchValue, experienceIds, sortField, sortOrder))
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }))
  }, [dispatch, id, pagination.pageSize, searchValue, selectedExperiences, sortParams])

  useEffect(() => {
    if (pagination.current === 1) return
    const filteredExperiences = selectedExperiences.filter((xId) => xId !== "all")
    const experienceIds = filteredExperiences.length > 0 ? filteredExperiences : null
    const sortField = Object.keys(sortParams)[0]
    const sortOrder = sortParams[sortField]
    dispatch(getSignedDocuments(id, pagination.current, pagination.pageSize, searchValue, experienceIds, sortField, sortOrder))
  }, [pagination.current])

  const formattedItems = useMemo(
    () =>
      items.map((item, index) => {
        const date = new Date(item.date)
        const formattedDate = date.toLocaleDateString()
        const waiverNames = parseWaiverNames(item.waiver_name)
        const firstWaiverName = waiverNames[0] || "N/A"

        return {
          ...item,
          key: item.id || item.uuid || `${item.booking_id}-${index}`,
          dateTime: `${formattedDate} | ${item.start_time}`,
          waiverNameSummary: waiverNames.length > 1 ? `${firstWaiverName} +${waiverNames.length - 1}` : firstWaiverName,
          waiverStatus: item.waiver_status || "Unsigned"
        }
      }),
    [items]
  )

  const mobileItems = useMemo(
    () =>
      formattedItems.map((item) => {
        const waiverNames = parseWaiverNames(item.waiver_name)
        const firstName = waiverNames[0] || "N/A"
        const count = waiverNames.length
        const waiverName =
          count > 1 ? (
            <Flex flex={1} align="center">
              {firstName}
              <Button
                type="link"
                className={styles.waiverCount}
                onClick={(e) => {
                  e.stopPropagation()
                  handleWaiverNamesClick(item.waiver_name, item)
                }}
              >
                +{count - 1}
              </Button>
            </Flex>
          ) : (
            firstName
          )
        return {
          id: item.key,
          experienceDateTime: item.dateTime,
          guestName: item.guest_name,
          experienceName: item.experience_name,
          bookingId: item.booking_id,
          waiverName,
          status: item.waiverStatus,
          rawData: item
        }
      }),
    [formattedItems, handleWaiverNamesClick]
  )

  const columns = [
    {
      title: "Experience Date & Time",
      key: "dateTime",
      dataIndex: "date",
      sorter: true,
      render: (_, record) => {
        const date = new Date(record.date)
        const formattedDate = date.toLocaleDateString()
        return `${formattedDate} | ${record.start_time}`
      }
    },
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      key: "guest_name",
      sorter: true
    },
    {
      title: "Experience Name",
      dataIndex: "experience_name",
      key: "experience_name",
      sorter: true,
      render: (experienceName) => (
        <span title={experienceName}>{experienceName?.length > 15 ? `${experienceName.substring(0, 12)}...` : experienceName}</span>
      )
    },
    {
      title: "Booking ID",
      dataIndex: "booking_id",
      key: "booking_id",
      sorter: true,
      render: (bookingId) => <span className={styles.bookedId}>{bookingId}</span>
    },
    {
      title: "Waiver Name",
      dataIndex: "waiver_name",
      key: "waiver_name",
      render: (waiverName, record) => {
        if (!waiverName) return "N/A"

        const waiverNames = parseWaiverNames(waiverName)
        const firstName = waiverNames[0]
        const count = waiverNames.length

        if (count === 1) {
          return firstName
        }

        return (
          <span>
            {firstName}
            <Button type="link" className={styles.waiverCount} onClick={(e) => { e.stopPropagation(); handleWaiverNamesClick(waiverName, record) }}>
              +{count - 1}
            </Button>
          </span>
        )
      }
    },
    {
      title: "Status",
      dataIndex: "waiver_status",
      key: "waiver_status",
      render: (status) => {
        const statusClass = status === "Signed" ? styles.statusSigned : styles.statusUnsigned
        return <span className={statusClass}>{status || "Unsigned"}</span>
      }
    }
  ]
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      current: 1 // Reset to first page when page size changes
    }))
  }

  return (
    <div>
      <WithEmptyStateWrapper
        fallback={
          loading ? (
            <Card className={`w-full ${styles.cardWrapper}`}>
              <Spin />
            </Card>
          ) : (
            <Card className={`w-full ${styles.cardWrapper}`}>
              <Empty
                image={searchValue ? <SearchIcon /> : <DigitalWaiverEmptyImage />}
                description={
                  <Flex vertical gap={12} align="center" direction="column">
                    <Text size="lg">{searchValue ? "No exact matches." : "No signed documents at the moment"}</Text>
                  </Flex>
                }
              />
            </Card>
          )
        }
        hasItems={mobileItems.length > 0}
        name="signedDocument"
        tag="div"
      >
        {isCompact ? (
          <DataTableResponsive
            data={mobileItems}
            onItemClick={(record) => handleRowClick(null, record.rawData)}
            fieldOrder={["experienceDateTime", "guestName", "experienceName", "bookingId", "waiverName", "status"]}
            labelMap={{
              experienceDateTime: "Experience Date & Time",
              guestName: "Guest Name",
              experienceName: "Experience Name",
              bookingId: "Booking ID",
              waiverName: "Waiver Name",
              status: "Status"
            }}
          />
        ) : (
          <DataTable
            columns={columns}
            dataSource={formattedItems}
            rowKey="key"
            onRowClick={handleRowClick}
            onChange={handleTableChange}
            pagination={false}
            loading={loading}
          />
        )}
      </WithEmptyStateWrapper>
      <div className="mt-4">
        <CustomPagination
          metadata={metadata}
          pageSize={pagination.pageSize}
          total={metadata?.total_results || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <Modal
        title="All Waiver Names"
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={400}
        centered
        maskClosable={false}
      >
        <Spin spinning={docsLoading}>
        <List
          dataSource={selectedWaiverNames}
          renderItem={(name, index) => {
            const doc = bookingDocs[index]
            const url = doc?.url || (typeof doc === "string" ? doc : null)
            return (
              <List.Item
                key={index}
                actions={url ? [
                  <DownloadOutlined
                    key="download"
                    className={styles.downloadIcon}
                    onClick={async () => {
                      try {
                        const response = await fetch(url)
                        if (!response.ok) throw new Error("Network response was not ok")
                        const blob = await response.blob()
                        const objectUrl = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = objectUrl
                        link.download = name || `document-${index + 1}.pdf`
                        document.body.appendChild(link)
                        link.click()
                        setTimeout(() => {
                          link.remove()
                          window.URL.revokeObjectURL(objectUrl)
                        }, 100)
                      } catch {
                        window.open(url, "_blank")
                      }
                    }}
                  />
                ] : []}
              >
                {name}
              </List.Item>
            )
          }}
        />
        </Spin>
      </Modal>
    </div>
  )
}

export default SignedDocumentsTable

SignedDocumentsTable.propTypes = {
  searchValue: PropTypes.string,
  selectedExperiences: PropTypes.array
}
